import type { CapacityContributionRecordPartner } from '@navio/nsdk';
import {
  CapacityContributionLocationTypeLabels,
  CapacityContributionStatus,
  CapacityContributionType
} from '@navio/nsdk';
import { getUserDisplayName } from '../user/user';

export type ContributionDaySupply = {
  /**
   * Registered personal seat-equivalents that day (sum of parallel units across
   * contributions). Not peak concurrent staffing — non-overlapping shifts add.
   */
  personalCount: number;
  /**
   * Registered baseline seat-equivalents that day (sum of parallel units).
   * Same sum semantics as personalCount — not peak concurrent.
   */
  baselineCount: number;
  personalSeatHours: number;
  baselineSeatHours: number;
};

type SupplySeatCounts = {
  personalCount: number;
  baselineCount: number;
};

/**
 * Statuses that still count as supply on capacity boards / Gantt.
 *
 * Product rule: once a contribution is past draft/pending and not withdrawn or
 * declined, it is registered supply — including WAIT_LIST (queued but still
 * holding seats) and RESERVED. Withdrawn, declined, and pending do not count.
 * Scope server-side via `statusIds`; client filter is a safety net only.
 */
export const ACTIVE_CAPACITY_SUPPLY_STATUS_IDS: CapacityContributionStatus[] = [
  CapacityContributionStatus.SUBMITTED,
  CapacityContributionStatus.WAIT_LIST,
  CapacityContributionStatus.RESERVED,
  CapacityContributionStatus.UNASSIGNED,
  CapacityContributionStatus.ASSIGNED
];

const activeSupplyStatusSet = new Set<number>(ACTIVE_CAPACITY_SUPPLY_STATUS_IDS);

export const isActiveCapacitySupply = (c: Pick<CapacityContributionRecordPartner, 'status'>) =>
  activeSupplyStatusSet.has(c.status.value);

type TimeWindow = { startsAt: string; endsAt: string };

type ContributionSecondsInput = Pick<CapacityContributionRecordPartner, 'startsAt' | 'endsAt' | 'parallelUnits'> & {
  intervals?: TimeWindow[] | null;
};

export const contributionTimeWindows = (c: ContributionSecondsInput): TimeWindow[] =>
  c.intervals && c.intervals.length > 0 ? c.intervals : [{ startsAt: c.startsAt, endsAt: c.endsAt }];

const windowSeconds = (startsAt: string, endsAt: string) => {
  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return 0;
  }
  return (end - start) / 1000;
};

/**
 * Seat-seconds of supply for a contribution: sum of working windows × parallel units.
 * Prefer `intervals` (gaps between windows are not capacity); fall back to envelope starts/ends.
 */
export const contributionSeconds = (c: ContributionSecondsInput) => {
  const units = Math.max(1, c.parallelUnits ?? 1);
  const seconds = contributionTimeWindows(c).reduce(
    (sum, window) => sum + windowSeconds(window.startsAt, window.endsAt),
    0
  );
  return seconds * units;
};

/** Worker-equivalents represented by this contribution (baseline seats or 1 personal worker). */
const contributionParallelUnits = (c: Pick<CapacityContributionRecordPartner, 'parallelUnits'>) =>
  Math.max(1, c.parallelUnits ?? 1);

/**
 * Seat-equivalents for one contribution. Personal and baseline both use parallel units
 * so horizon boards, mobile aggregation, and slot cards share one counting model.
 */
export const supplySeatCountsFromContribution = (
  contribution: Pick<CapacityContributionRecordPartner, 'contributionType' | 'parallelUnits'>
): SupplySeatCounts => {
  const units = contributionParallelUnits(contribution);
  if (contribution.contributionType === CapacityContributionType.BASELINE) {
    return { personalCount: 0, baselineCount: units };
  }
  return { personalCount: units, baselineCount: 0 };
};

const contributionDisplayName = (
  contribution: Pick<CapacityContributionRecordPartner, 'id' | 'contributionType' | 'parallelUnits' | 'user'>
) => {
  if (contribution.contributionType === CapacityContributionType.BASELINE) {
    const units = contributionParallelUnits(contribution);
    return units > 1 ? `Expected staffing (${units} seats)` : `Expected staffing #${contribution.id}`;
  }
  if (contribution.user) {
    return getUserDisplayName(contribution.user);
  }
  return `Contribution ${contribution.id}`;
};

/**
 * Day → registered seat-equivalents (sum of parallel units / seat-hours).
 * Not peak concurrent capacity: non-overlapping shifts on the same day add.
 */
export const buildContributionSupplyByDate = (
  contributions: CapacityContributionRecordPartner[],
  garageId?: number
): Map<string, ContributionDaySupply> => {
  const map = new Map<string, ContributionDaySupply>();

  for (const c of contributions) {
    if (!isActiveCapacitySupply(c)) {
      continue;
    }
    if (garageId != null) {
      const garageIds = c.garageIds ?? [];
      if (!garageIds.includes(garageId)) {
        continue;
      }
    }

    const existing = map.get(c.date) ?? {
      personalCount: 0,
      baselineCount: 0,
      personalSeatHours: 0,
      baselineSeatHours: 0
    };
    const hours = contributionSeconds(c) / 3600;
    const counts = supplySeatCountsFromContribution(c);
    existing.personalCount += counts.personalCount;
    existing.baselineCount += counts.baselineCount;
    if (c.contributionType === CapacityContributionType.BASELINE) {
      existing.baselineSeatHours += hours;
    } else {
      existing.personalSeatHours += hours;
    }
    map.set(c.date, existing);
  }

  return map;
};

type ContributionGanttSegment = {
  startsAt: string;
  endsAt: string;
};

type ContributionGanttLane = {
  /** Contribution id — used for deep-links. */
  id: number;
  /** Unique within a render. */
  laneKey: string;
  label: string;
  isBaseline: boolean;
  date: string;
  /** Working windows on this lane (gaps between them are not drawn as supply). */
  segments: ContributionGanttSegment[];
  capabilities: { id: number; name: string }[];
  garageNames: string[];
  locationType: string;
};

/**
 * One Gantt row per contribution. Multi-interval shifts draw one bar per working
 * window on the same row so gaps between windows are visible and not painted as supply.
 */
export const toContributionGanttLanes = (contributions: CapacityContributionRecordPartner[]): ContributionGanttLane[] =>
  [...contributions]
    .filter(isActiveCapacitySupply)
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    .map((c) => {
      const isBaseline = c.contributionType === CapacityContributionType.BASELINE;
      const label = contributionDisplayName(c);
      return {
        id: c.id,
        laneKey: String(c.id),
        label,
        isBaseline,
        date: c.date,
        segments: contributionTimeWindows(c),
        capabilities: c.capabilities.map((cap) => ({ id: cap.id, name: cap.name })),
        garageNames: c.garageNames ?? [],
        locationType: CapacityContributionLocationTypeLabels[c.locationType]
      };
    });

/** True when a pageSize-capped list response is incomplete. */
export const isListResponseTruncated = (count: number | undefined | null, resultsLength: number) =>
  count != null && count > resultsLength;
